package dev.akshaychordiya.pages.articles

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initIntroducingPoseLibraryPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("articles/IntroducingPoseLibrary.md", FrontMatterElement.Builder { addScalar("title", "Introducing Pose library 📸"); addScalar("description", "Auto-generate Jetpack Compose @Preview functions from your @Composable code at build-time, deterministic, offline, no LLM."); addScalar("date", "2026-09-14"); addList("tags") { addScalar("android"); addScalar("compose"); addScalar("kotlin"); addScalar("ksp"); addScalar("library"); addScalar("open-source"); }; addScalar("image", "/images/articles/introducing-pose-library/thumbnail.png"); }))
}

@Page("/articles/introducing-pose-library")
@Layout(".components.layouts.ArticleLayout")
@Composable
fun IntroducingPoseLibraryPage() {
    com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("img", attrs = {attr("src", "/images/articles/introducing-pose-library/thumbnail.png");attr("alt", "Pose Icon Thumbnail");attr("width", "128")})
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("One afternoon I was updating Compose previews. Again. Fourth time that day. I wasn't even writing them myself. I'd tell the LLM ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("\"you missed updating the preview 😅\"")
        }
        org.jetbrains.compose.web.dom.Text(", which kind of gave the game away.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("A preview is a function, an annotation, and some dummy data.")
        }
        org.jetbrains.compose.web.dom.Text(" If an LLM can write it from the signature alone, so can the compiler.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So I built ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/AkshayChordiya/Pose") {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Pose")
            }
        }
        org.jetbrains.compose.web.dom.Text(" 🏞️")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.graphics.Image("https://raw.githubusercontent.com/AkshayChordiya/Pose/main/docs/images/pose-demo.gif", "Pose generating a Compose preview at build time")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("one-annotation-every-state") }) {
        org.jetbrains.compose.web.dom.Text("One annotation. Every state. 🎬")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("sealed interface SearchState {\n    data object Loading : SearchState\n    data object NoResults : SearchState\n    data class Results(val query: String, val hitCount: Int) : SearchState\n}\n\n@Pose\n@Composable\nfun SearchContent(state: SearchState, onRetry: () -> Unit) { /* … */ }\n") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That's the whole diff. ☝️ One line. Next build, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("build/generated/")
        }
        org.jetbrains.compose.web.dom.Text(" has this:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("@Composable\n@Preview(name = \"Light\", uiMode = 16, showBackground = true)\n@Preview(name = \"Dark\", uiMode = 32, showBackground = true)\ninternal fun SearchContent__Preview_Results() {\n    CompositionLocalProvider(LocalInspectionMode provides true) {\n        AppPose.Theme {\n            SearchContent(\n                state = SearchState.Results(query = \"Query\", hitCount = 0),\n                onRetry = {},\n            )\n        }\n    }\n}\n// …plus SearchContent__Preview_Loading and SearchContent__Preview_NoResults\n") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Pose saw a sealed type and ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("fanned it out")
        }
        org.jetbrains.compose.web.dom.Text(". One preview per subtype, each named after it, in light and dark. 🎨")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Now the good part:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("You do this")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Pose does this")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Add a 4th state")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("A 4th preview appears ➕")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Rename ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("onRetry")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Regenerates, nothing breaks 🔄")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Delete the composable")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Preview vanishes 🗑️")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Nothing")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("They're still correct ✅")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("It was never yours to maintain.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("then-it-gets-silly") }) {
        org.jetbrains.compose.web.dom.Text("Then it gets silly 📸")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Paparazzi, Roborazzi and Google's screenshot plugin all scan for ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@Preview")
        }
        org.jetbrains.compose.web.dom.Text(". Generated previews compile down to exactly the same thing as handwritten ones.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So they get picked up. Automatically:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("1 annotation → 3 previews → 6 renders → 6 screenshot tests\n") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Zero test code.")
        }
        org.jetbrains.compose.web.dom.Text(" You annotated a composable and got visual regression coverage of every state in both themes.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("And it can't drift. Previews regenerate every build, so a golden can never quietly test last month's UI. 🔒")
    }
    org.jetbrains.compose.web.dom.Blockquote {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("🕵️ One reason this actually works: Studio sets ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("LocalInspectionMode")
            }
            org.jetbrains.compose.web.dom.Text(", snapshot runners don't. A composable that swaps a network image for a placeholder takes its ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("production")
            }
            org.jetbrains.compose.web.dom.Text(" path in a screenshot test and renders blank. Pose provides the local itself, so both agree.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("whole-module-one-line") }) {
        org.jetbrains.compose.web.dom.Text("Whole module, one line 🚀")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("@PoseSetup(generateForAllPublicComposables = true)\n") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every public composable in the module. Previewed. Screenshot-tested. Skips anything with a handwritten ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@Preview")
        }
        org.jetbrains.compose.web.dom.Text(", anything marked ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@PoseIgnore")
        }
        org.jetbrains.compose.web.dom.Text(", and wrapper-shaped things like themes. Awkward composables become warnings, never build failures.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("your-data-everywhere-you-use-it") }) {
        org.jetbrains.compose.web.dom.Text("Your data, everywhere you use it 🧪")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Placeholders are fine until you need the ugly cases. Drop this on the type:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("companion object {\n    val previewSamples: Sequence<Track> get() = sequenceOf(\n        Track(title = \"Short\"),\n        Track(title = \"A title long enough to wrap onto two lines\"),\n        Track(title = \"\", explicit = true),\n    )\n}\n") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Now ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("every")
        }
        org.jetbrains.compose.web.dom.Text(" composable taking a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Track")
        }
        org.jetbrains.compose.web.dom.Text(" renders all three. Write it once, every screen in the app benefits. Wired through ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@PreviewParameter")
        }
        org.jetbrains.compose.web.dom.Text(" for you. ✨")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("don-t-own-the-type") }) {
        org.jetbrains.compose.web.dom.Text("Don't own the type?")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@Pose(providers = [TrackSamples::class])")
        }
        org.jetbrains.compose.web.dom.Text(", matched by generic type.")
    }
    org.jetbrains.compose.web.dom.Blockquote {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("💡 Note the ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("get() = sequenceOf(...)")
            }
            org.jetbrains.compose.web.dom.Text(" builds its arguments eagerly, a ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Sequence")
            }
            org.jetbrains.compose.web.dom.Text(" is lazy about ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("iteration")
            }
            org.jetbrains.compose.web.dom.Text(", not construction. A plain ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("val")
            }
            org.jetbrains.compose.web.dom.Text(" would construct every sample in production, for data only the preview panel reads.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("setup") }) {
        org.jetbrains.compose.web.dom.Text("Setup ⚡")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two blocks. That's it.")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("dependencies {\n    implementation(\"io.github.akshaychordiya.pose:annotations:<latest>\")\n    kspDebug(\"io.github.akshaychordiya.pose:processor:<latest>\")\n}\n") } }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("@PoseSetup\ninternal object AppPose : PoseConfig {\n    @Composable\n    override fun Theme(content: @Composable () -> Unit) {\n        AppTheme { content() }\n    }\n}\n") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("No strings anywhere.")
        }
        org.jetbrains.compose.web.dom.Text(" Pose never learns your theme's ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("name")
        }
        org.jetbrains.compose.web.dom.Text(", it emits ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("AppPose.Theme { … }")
        }
        org.jetbrains.compose.web.dom.Text(" and lets the compiler resolve it. Rename ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("AppTheme")
        }
        org.jetbrains.compose.web.dom.Text(" and the IDE fixes this file too. ✅")
    }
    org.jetbrains.compose.web.dom.Blockquote {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("📌 ")
            com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/AkshayChordiya/Pose/releases") {
                org.jetbrains.compose.web.dom.Text("Check the latest version")
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Want a preview matrix instead of light/dark? ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@PoseSetup(previews = [AppPreviews::class])")
        }
        org.jetbrains.compose.web.dom.Text(". Font scale, RTL, tablet, all in one place, one edit for the whole module. 🎛️")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Works on ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Android and Compose Multiplatform")
        }
        org.jetbrains.compose.web.dom.Text(" 🌍")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("can-t-i-just-use") }) {
        org.jetbrains.compose.web.dom.Text("\"Can't I just use…?\" 🤔")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("You might be thinking why can't I just use LLM or IDE plugins to generate previews? Well you can!")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th()
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Writes into")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Survives a rename")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Studio's Generate Preview")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("your source")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("❌ you own it now")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("IDE plugins")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("your source")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("❌")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Asking an LLM")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("your source")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("❌")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("Pose")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("build/generated/")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("✅")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("They all solve ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("authoring")
        }
        org.jetbrains.compose.web.dom.Text(". Authoring was never hard, the first preview takes thirty seconds. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The cost is everything after.")
        }
        org.jetbrains.compose.web.dom.Text(" You don't want a tool that writes previews. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("You want to not own them.")
        }
        org.jetbrains.compose.web.dom.Text(" 🎯")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("there-s-an-ide-plugin-too") }) {
        org.jetbrains.compose.web.dom.Text("There's an IDE plugin too 🎨")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Generated code lives in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("build/")
        }
        org.jetbrains.compose.web.dom.Text(", so you won't automatically see the previews in your source. So the IntelliJ plugin shows a gutter icon to your Composables which takes you to the previews. The ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://plugins.jetbrains.com/plugin/33344-pose--auto-generate-compose-previews") {
            org.jetbrains.compose.web.dom.Text("companion plugin")
        }
        org.jetbrains.compose.web.dom.Text(" adds one. Click it, land on the generated preview, with a picker when a sealed type fanned out.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("It is entirely optional just to make it easier to connect the autogenerated previews and your ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@Composable")
        }
        org.jetbrains.compose.web.dom.Text(". The processor works fine alone.")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.graphics.Image("https://raw.githubusercontent.com/AkshayChordiya/Pose/refs/heads/main/docs/images/gutter-icon.png", "Gutter icon in IntelliJ / Android Studio")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("try-it") }) {
        org.jetbrains.compose.web.dom.Text("Try it 🚀")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Ready to strike a pose? 📸")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Check out ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/AkshayChordiya/Pose") {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Pose on GitHub")
            }
        }
        org.jetbrains.compose.web.dom.Text(" and stop manually maintaining your previews today! ✨")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
}
